/**
 * XEP-0050 ad-hoc client for per-stop route automations.
 *
 * The frontend never writes `urn:slickshift:account:workflows:0` itself — it
 * reads that node for display and asks the agent to perform every write, so
 * the agent's item lock stays the single writer and other people's workflows
 * in the same item survive. Same split the cron CRUD already uses.
 *
 * Ownership needs no field: the agent takes the owner from the IQ sender.
 */
import { Client } from "@xmpp/client"
import type { StopPhase } from "@/lib/stopAutomations"
import {
  formField,
  isNotFoundError,
  readResultField,
  sendCommand,
} from "./adhocForm"

export const CREATE_NODE = "stop_automation_create"
export const UPDATE_NODE = "stop_automation_update"
export const DELETE_NODE = "stop_automation_delete"

export type StopAutomationInput = {
  stopId: string
  phase: StopPhase
  message: string
  name?: string
  stopAction?: "pickup" | "delivery"
  targetJid?: string
}

export type StopAutomationPatch = {
  message?: string
  phase?: StopPhase
  name?: string
}

/**
 * Resolves with the workflow id the agent minted. That id is the same one the
 * pubsub item will carry, so the caller can match its optimistic state against
 * the node's own event.
 */
export const createStopAutomation = async (
  xmpp: Client,
  input: StopAutomationInput,
): Promise<string> => {
  const reply = await sendCommand(xmpp, CREATE_NODE, [
    formField("stop_id", input.stopId),
    formField("phase", input.phase),
    formField("message", input.message),
    formField("name", input.name),
    formField("stop_action", input.stopAction),
    formField("target_jid", input.targetJid),
  ])

  const id = readResultField(reply, "id")
  if (!id) throw new Error("stop_automation_create returned no workflow id")
  return id
}

export const updateStopAutomation = async (
  xmpp: Client,
  id: string,
  patch: StopAutomationPatch,
): Promise<void> => {
  await sendCommand(xmpp, UPDATE_NODE, [
    formField("id", id),
    formField("message", patch.message),
    formField("phase", patch.phase),
    formField("name", patch.name),
  ])
}

export const deleteStopAutomation = async (
  xmpp: Client,
  id: string,
): Promise<void> => {
  await sendCommand(xmpp, DELETE_NODE, [formField("id", id)])
}
